import { SectionHeader } from '../rows/SectionHeader'
import { MetricRow } from '../rows/MetricRow'
import { AverageCheckRow } from '../rows/AverageCheckRow'
import type { MonthMetrics } from '@/features/history/types'

interface RetentionSectionProps {
  metrics: MonthMetrics
  weeklyAvg: (v: number) => number
  formatNumber: (v: number) => string
  formatCurrency: (v: number) => string
  hasRegularRetentionData: boolean
  hasRenewalsCountData: boolean
  hasRenewalsAmountData: boolean
  hasRenewalsAvgCheckData: boolean
  hasUpgradesCountData: boolean
  hasUpgradesAmountData: boolean
  hasUpgradesAvgCheckData: boolean
  hasReturningClientsCountData: boolean
  hasReturningClientsAmountData: boolean
  hasReturningClientsAvgCheckData: boolean
  hasSubscriptionRetentionData: boolean
  hasSubscriptionRenewalsCountData: boolean
  hasSubscriptionRenewalsAmountData: boolean
  hasSubscriptionRenewalsAvgCheckData: boolean
  hasSubscriptionUpgradesCountData: boolean
  hasSubscriptionUpgradesAmountData: boolean
  hasSubscriptionUpgradesAvgCheckData: boolean
  hasMembershipFeesUpgradesData: boolean
  hasSubscriptionReturningClientsCountData: boolean
  hasSubscriptionReturningClientsAmountData: boolean
  hasSubscriptionReturningClientsAvgCheckData: boolean
  hasMembershipFeesReturningData: boolean
}

export function RetentionSection({
  metrics,
  weeklyAvg,
  formatNumber,
  formatCurrency,
  hasRegularRetentionData,
  hasRenewalsCountData,
  hasRenewalsAmountData,
  hasRenewalsAvgCheckData,
  hasUpgradesCountData,
  hasUpgradesAmountData,
  hasUpgradesAvgCheckData,
  hasReturningClientsCountData,
  hasReturningClientsAmountData,
  hasReturningClientsAvgCheckData,
  hasSubscriptionRetentionData,
  hasSubscriptionRenewalsCountData,
  hasSubscriptionRenewalsAmountData,
  hasSubscriptionRenewalsAvgCheckData,
  hasSubscriptionUpgradesCountData,
  hasSubscriptionUpgradesAmountData,
  hasSubscriptionUpgradesAvgCheckData,
  hasMembershipFeesUpgradesData,
  hasSubscriptionReturningClientsCountData,
  hasSubscriptionReturningClientsAmountData,
  hasSubscriptionReturningClientsAvgCheckData,
  hasMembershipFeesReturningData,
}: RetentionSectionProps) {
  const {
    renewals_count: renCount, renewals_amount: renAmt, renewals_avg_check: renAvg,
    upgrades_count: upgCount, upgrades_amount: upgAmt, upgrades_avg_check: upgAvg,
    returning_clients_count: retCount, returning_clients_amount: retAmt, returning_clients_avg_check: retAvg,
    subscription_renewals_count: subRenCount, subscription_renewals_amount: subRenAmt, subscription_renewals_avg_check: subRenAvg,
    subscription_upgrades_count: subUpgCount, subscription_upgrades_amount: subUpgAmt, subscription_upgrades_avg_check: subUpgAvg,
    membership_fees_subscription_upgrades: mfUpg,
    subscription_returning_clients_count: subRetCount, subscription_returning_clients_amount: subRetAmt,
    subscription_returning_clients_avg_check: subRetAvg,
    membership_fees_subscription_returning: mfRet,
  } = metrics

  return (
    <>
      {hasRegularRetentionData && (
        <>
          <SectionHeader label="Продления и апгрейды" color="orange" />
          {hasRenewalsCountData && (
            <MetricRow label="Продления (кол-во)" metric={renCount}
              weeklyPlan={weeklyAvg(renCount.plan)} weeklyFact={weeklyAvg(renCount.fact)}
              formatPlan={formatNumber} formatFact={formatNumber} />
          )}
          {hasRenewalsAmountData && (
            <MetricRow label="Продления (сумма)" metric={renAmt}
              weeklyPlan={weeklyAvg(renAmt.plan)} weeklyFact={weeklyAvg(renAmt.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
          {hasRenewalsAvgCheckData && <AverageCheckRow label="Средний чек" metric={renAvg} formatValue={formatCurrency} />}
          {hasUpgradesCountData && (
            <MetricRow label="Апгрейды (кол-во)" metric={upgCount}
              weeklyPlan={weeklyAvg(upgCount.plan)} weeklyFact={weeklyAvg(upgCount.fact)}
              formatPlan={formatNumber} formatFact={formatNumber} />
          )}
          {hasUpgradesAmountData && (
            <MetricRow label="Апгрейды (сумма)" metric={upgAmt}
              weeklyPlan={weeklyAvg(upgAmt.plan)} weeklyFact={weeklyAvg(upgAmt.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
          {hasUpgradesAvgCheckData && <AverageCheckRow label="Средний чек" metric={upgAvg} formatValue={formatCurrency} />}
          {hasReturningClientsCountData && (
            <MetricRow label="БЧС (кол-во)" metric={retCount}
              weeklyPlan={weeklyAvg(retCount.plan)} weeklyFact={weeklyAvg(retCount.fact)}
              formatPlan={formatNumber} formatFact={formatNumber} />
          )}
          {hasReturningClientsAmountData && (
            <MetricRow label="БЧС (сумма)" metric={retAmt}
              weeklyPlan={weeklyAvg(retAmt.plan)} weeklyFact={weeklyAvg(retAmt.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
          {hasReturningClientsAvgCheckData && <AverageCheckRow label="Средний чек" metric={retAvg} formatValue={formatCurrency} />}
        </>
      )}

      {hasSubscriptionRetentionData && (
        <>
          <SectionHeader label="Автопродления и переходы на подписку" color="orange" />
          {hasSubscriptionRenewalsCountData && (
            <MetricRow label="Автопродления подписок (кол-во)" metric={subRenCount}
              weeklyPlan={weeklyAvg(subRenCount.plan)} weeklyFact={weeklyAvg(subRenCount.fact)}
              formatPlan={formatNumber} formatFact={formatNumber} />
          )}
          {hasSubscriptionRenewalsAmountData && (
            <MetricRow label="Автопродления подписок (сумма)" metric={subRenAmt}
              weeklyPlan={weeklyAvg(subRenAmt.plan)} weeklyFact={weeklyAvg(subRenAmt.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
          {hasSubscriptionRenewalsAvgCheckData && <AverageCheckRow label="Средний чек" metric={subRenAvg} formatValue={formatCurrency} />}
          {hasSubscriptionUpgradesCountData && (
            <MetricRow label="Переходы на подписку (кол-во)" metric={subUpgCount}
              weeklyPlan={weeklyAvg(subUpgCount.plan)} weeklyFact={weeklyAvg(subUpgCount.fact)}
              formatPlan={formatNumber} formatFact={formatNumber} />
          )}
          {hasSubscriptionUpgradesAmountData && (
            <MetricRow label="Переходы на подписку (сумма)" metric={subUpgAmt}
              weeklyPlan={weeklyAvg(subUpgAmt.plan)} weeklyFact={weeklyAvg(subUpgAmt.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
          {hasSubscriptionUpgradesAvgCheckData && <AverageCheckRow label="Средний чек" metric={subUpgAvg} formatValue={formatCurrency} />}
          {hasMembershipFeesUpgradesData && (
            <MetricRow label="Членские взносы при переходах" metric={mfUpg}
              weeklyPlan={weeklyAvg(mfUpg.plan)} weeklyFact={weeklyAvg(mfUpg.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
          {hasSubscriptionReturningClientsCountData && (
            <MetricRow label="БЧС подписок (кол-во)" metric={subRetCount}
              weeklyPlan={weeklyAvg(subRetCount.plan)} weeklyFact={weeklyAvg(subRetCount.fact)}
              formatPlan={formatNumber} formatFact={formatNumber} />
          )}
          {hasSubscriptionReturningClientsAmountData && (
            <MetricRow label="БЧС подписок (сумма)" metric={subRetAmt}
              weeklyPlan={weeklyAvg(subRetAmt.plan)} weeklyFact={weeklyAvg(subRetAmt.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
          {hasSubscriptionReturningClientsAvgCheckData && <AverageCheckRow label="Средний чек" metric={subRetAvg} formatValue={formatCurrency} />}
          {hasMembershipFeesReturningData && (
            <MetricRow label="Членские взносы БЧС" metric={mfRet}
              weeklyPlan={weeklyAvg(mfRet.plan)} weeklyFact={weeklyAvg(mfRet.fact)}
              formatPlan={formatCurrency} formatFact={formatCurrency} />
          )}
        </>
      )}
    </>
  )
}
